/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2, TrendingUp, Users, Sparkles, Send, Activity } from 'lucide-react';
import EcosystemBadge from '@/components/terra/EcosystemBadge';

const BAND_TONE = {
  IDEAL:     'bg-emerald-100 text-emerald-800 border-emerald-300',
  STRONG:    'bg-sky-100 text-sky-800 border-sky-300',
  NURTURE:   'bg-amber-100 text-amber-800 border-amber-300',
  HIGH_RISK: 'bg-rose-100 text-rose-800 border-rose-300',
};

export default function GrowthCommandCenter() {
  const [summary, setSummary] = useState(null);
  const [ecosystems, setEcosystems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, e] = await Promise.all([
          axios.get(`${API}/terra-growth/command-center/summary`),
          axios.get(`${API}/terra-growth/ecosystems`),
        ]);
        setSummary(s.data);
        setEcosystems(e.data?.ecosystems || []);
      } catch {
        // surfaced quietly
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const ecoMap = ecosystems.reduce((m, e) => { m[e.code] = e; return m; }, {});

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="growth-command-center" className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <p className="text-[10px] uppercase tracking-[0.20em] text-[#8C6239] font-['Work_Sans']">
            Terra Growth OS™ · Strategic Command
          </p>
          <h1 className="text-3xl font-['Outfit'] font-light text-[#2C2A29] mt-1">
            Growth Command Center
          </h1>
          <p className="text-sm text-[#8A8681] font-['Work_Sans'] mt-1 max-w-2xl">
            Territory pulse — perception movement, RSS quality, ecosystem influence, referral conversion.
            Optimised for right-subscriber depth, not lead volume.
          </p>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Loading…
          </div>
        ) : !summary ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            Could not load summary.
          </div>
        ) : (
          <>
            {/* Top metric strip */}
            <div data-testid="cc-metric-strip" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <MetricTile
                testId="cc-metric-leads" icon={<Users className="w-4 h-4" />}
                label="Leads in pipeline" value={summary.total_leads}
              />
              <MetricTile
                testId="cc-metric-rss-avg" icon={<Sparkles className="w-4 h-4" />}
                label="RSS overall avg"
                value={summary.rss?.overall_avg !== null ? summary.rss.overall_avg : '—'}
              />
              <MetricTile
                testId="cc-metric-referral-conv" icon={<Send className="w-4 h-4" />}
                label="Referral conversion"
                value={`${summary.referrals?.conversion_rate_pct ?? 0}%`}
                sub={`${summary.referrals?.converted ?? 0} / ${summary.referrals?.total ?? 0}`}
              />
              <MetricTile
                testId="cc-metric-perception-7d" icon={<TrendingUp className="w-4 h-4" />}
                label="Perception shifts · 7d"
                value={summary.recent_perception_transitions_7d}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Perception buckets */}
              <section data-testid="cc-perception-section"
                       className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
                <h2 className="text-base font-['Outfit'] font-light text-[#2C2A29] mb-1">
                  Perception distribution
                </h2>
                <p className="text-[11px] text-[#8A8681] mb-4">Where the territory sits across the 12-state ladder.</p>
                <ul className="space-y-2">
                  {Object.entries(summary.perception?.buckets || {})
                    .sort(([, a], [, b]) => b - a)
                    .map(([state, count]) => {
                      const total = summary.total_leads || 1;
                      const pct = Math.round((count / total) * 100);
                      return (
                        <li key={state} data-testid={`cc-perception-row-${state}`}
                            className="flex items-center gap-3">
                          <span className="text-[11px] uppercase tracking-[0.14em] text-[#5C5955] w-44 truncate">
                            {summary.perception?.labels?.[state] || state}
                          </span>
                          <div className="flex-1 h-2 rounded-full bg-[#F4F2EE] overflow-hidden">
                            <div className="h-full bg-[#4A5D4E]" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[11px] tabular-nums text-[#5C5955] w-10 text-right">{count}</span>
                        </li>
                      );
                    })}
                </ul>
              </section>

              {/* RSS bands */}
              <section data-testid="cc-rss-section"
                       className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
                <h2 className="text-base font-['Outfit'] font-light text-[#2C2A29] mb-1">
                  Right Subscriber Score · band distribution
                </h2>
                <p className="text-[11px] text-[#8A8681] mb-4">
                  Optimised for ecosystem quality. Ideal &amp; Strong are the right subscribers.
                </p>
                {(summary.rss?.bands || []).length === 0 ? (
                  <p className="text-xs text-[#8A8681]">No RSS scores recorded yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {(summary.rss.bands || []).map(b => (
                      <li key={b.band} data-testid={`cc-rss-band-${b.band}`}
                          className="flex items-center justify-between gap-3 rounded-xl border border-[#EBE8E3] px-3 py-2">
                        <span className={`text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border ${BAND_TONE[b.band] || ''}`}>
                          {b.band.replace('_', ' ')}
                        </span>
                        <span className="text-[11px] text-[#5C5955]">avg {b.avg}</span>
                        <span className="text-sm tabular-nums text-[#2C2A29]">{b.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Top ecosystems */}
              <section data-testid="cc-ecosystem-section"
                       className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
                <h2 className="text-base font-['Outfit'] font-light text-[#2C2A29] mb-1">
                  Top relationship ecosystems
                </h2>
                <p className="text-[11px] text-[#8A8681] mb-4">
                  Where new right subscribers are entering from. Trust depth &gt; volume.
                </p>
                {(summary.top_ecosystems || []).length === 0 ? (
                  <p className="text-xs text-[#8A8681]">
                    No ecosystem-tagged leads yet. Tag leads as they enter to start mapping influence.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {summary.top_ecosystems.map(e => (
                      <li key={e.code} data-testid={`cc-ecosystem-row-${e.code}`}
                          className="flex items-center justify-between rounded-xl border border-[#EBE8E3] px-3 py-2">
                        <EcosystemBadge ecosystem={ecoMap[e.code] || { code: e.code, name: e.code }} />
                        <span className="text-sm tabular-nums text-[#2C2A29]">{e.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Referrals */}
              <section data-testid="cc-referral-section"
                       className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
                <h2 className="text-base font-['Outfit'] font-light text-[#2C2A29] mb-1">
                  Referral health
                </h2>
                <p className="text-[11px] text-[#8A8681] mb-4">
                  Multi-level referral graph view ships in Phase 3. Pipeline health below.
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <Stat label="Total" value={summary.referrals?.total ?? 0} />
                  <Stat label="Converted" value={summary.referrals?.converted ?? 0} />
                  <Stat label="Conversion %" value={`${summary.referrals?.conversion_rate_pct ?? 0}%`} />
                </div>
              </section>
            </div>
          </>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

function MetricTile({ testId, icon, label, value, sub }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-[#EBE8E3] bg-white p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-[#8A8681]">
        {icon} {label}
      </div>
      <div className="text-3xl font-['Outfit'] font-light text-[#2C2A29] tabular-nums mt-1">
        {value}
      </div>
      {sub && <div className="text-[11px] text-[#B5B0A8]">{sub}</div>}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-[#EBE8E3] p-3 text-center">
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8681]">{label}</div>
      <div className="text-xl font-['Outfit'] text-[#2C2A29] tabular-nums mt-1">{value}</div>
    </div>
  );
}
